import { resolveInclusiveFr } from '~/utils/inclusiveFr'

type Locale = 'fr' | 'en'

const LOCALE_STORAGE_KEY = 'poke-idle-locale'
const INCLUSIVE_STORAGE_KEY = 'poke-idle-inclusive-writing'

const locale = ref<Locale>('fr')
/** Préférence invité (écran login avant connexion) — synchronisée avec localStorage. */
const guestInclusiveWriting = ref(false)
let localeHydratedFromStorage = false

function applyDocumentLang(l: Locale) {
  if (!import.meta.client) return
  document.documentElement.lang = l
}

function readStoredLocaleClient(): Locale | null {
  if (!import.meta.client) return null
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (raw === 'en' || raw === 'fr') return raw
  } catch { /* ignore */ }
  return null
}

function readStoredInclusiveClient(): boolean {
  if (!import.meta.client) return false
  try {
    return localStorage.getItem(INCLUSIVE_STORAGE_KEY) === '1'
  } catch { /* ignore */ }
  return false
}

function writeStoredInclusiveClient(enabled: boolean) {
  if (!import.meta.client) return
  try {
    localStorage.setItem(INCLUSIVE_STORAGE_KEY, enabled ? '1' : '0')
  } catch { /* ignore */ }
}

export function readInclusiveWritingFromStorage(): boolean {
  return readStoredInclusiveClient()
}

/** Source unique pour t() / frText : compte connecté → store joueur, sinon invité. */
function isInclusiveWritingActive(): boolean {
  if (!import.meta.client) return false
  try {
    const player = usePlayerStore()
    if (player.isLoggedIn) return player.inclusiveWriting
  } catch { /* pinia pas prêt */ }
  return guestInclusiveWriting.value
}

/** Met à jour localStorage + ref invité ; met aussi à jour le store joueur si connecté. */
export function persistInclusiveWriting(enabled: boolean) {
  const on = Boolean(enabled)
  guestInclusiveWriting.value = on
  writeStoredInclusiveClient(on)
  if (!import.meta.client) return
  try {
    const player = usePlayerStore()
    if (player.isLoggedIn) player.setInclusiveWriting(on)
  } catch { /* pinia pas prêt */ }
}

/** Après chargement serveur : le compte fait foi, on aligne l’invité / localStorage. */
export function syncInclusiveFromPlayerAccount(enabled: boolean) {
  const on = Boolean(enabled)
  guestInclusiveWriting.value = on
  writeStoredInclusiveClient(on)
}

/** Locale des nombres affichés : suit la langue de l'interface (fr-FR par défaut). */
export function getUiNumberLocale(): string {
  return locale.value === 'en' ? 'en-US' : 'fr-FR'
}

export function useLocale() {
  if (import.meta.client && !localeHydratedFromStorage) {
    localeHydratedFromStorage = true
    const stored = readStoredLocaleClient()
    if (stored) locale.value = stored
    guestInclusiveWriting.value = readStoredInclusiveClient()
    applyDocumentLang(locale.value)
  }

  const inclusiveWriting = computed(() => isInclusiveWritingActive())

  function setLocale(l: Locale) {
    locale.value = l
    applyDocumentLang(l)
    try {
      if (import.meta.client) localStorage.setItem(LOCALE_STORAGE_KEY, l)
    } catch { /* ignore */ }
  }

  /**
   * @param fr — français standard
   * @param en — anglais
   * @param frInclusive — variante inclusive explicite (sinon transformation automatique)
   */
  function t(fr: string, en: string, frInclusive?: string): string {
    if (locale.value === 'en') return en
    if (!isInclusiveWritingActive()) return fr
    return resolveInclusiveFr(fr, frInclusive)
  }

  /** Texte FR brut (données labelFr/descFr) avec inclusive si activé. */
  function frText(fr: string, frInclusive?: string): string {
    if (locale.value !== 'fr') return fr
    if (!isInclusiveWritingActive()) return fr
    return resolveInclusiveFr(fr, frInclusive)
  }

  return {
    locale: readonly(locale),
    inclusiveWriting,
    setLocale,
    t,
    frText,
  }
}
